import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { ProductApiService } from '../../services/api/product-api.service';
import { PlatformService } from '../../../../core/services/platform/platform.service';
import { CategoryService } from '../../../../core/services/category/category.service';
import { UpdateStockModalComponent, UpdateStockModalModel } from '../../components/update-stock-modal/update-stock-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { SyncProductDialogComponent } from '../../components/sync-product-dialog/sync-product-dialog.component';
import { AuthService } from '../../../../core/services/auth/auth.service';
import { faBoxesStacked } from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'app-product-stock',
  templateUrl: './product-stock.component.html',
  styleUrl: './product-stock.component.css'
})
export class ProductStockComponent implements OnInit {
  faBoxesStacked = faBoxesStacked;
  loading: boolean = false;
  form: FormGroup;
  branchMode: boolean;

  countDataSource = 0;
  pageSize = 10;
  pageIndex = 0;
  limit = 10;
  offset = 0;

  datasets: any[] = []
  categories: Array<{ id: string; name: string; }> = this.categoryService?.categories || []
  products: Array<{ sku: string; name: string; category: Object; in_stock: number; prices: Object; }> = []
  stockPlatform: any[] = []
  channels: any[] = [];
  channelsHeaders: any[] = []

  channelSelected: string = '';
  branchKeyword: string = '';
  branchesFilter: any[] = [];
  showBranchFilter: boolean = false;


  constructor(
    private fb: FormBuilder,
    private productApiService: ProductApiService,
    private platformService: PlatformService,
    private categoryService: CategoryService,
    public dialog: MatDialog,
    private authService: AuthService,
    router: Router,
  ) {
    this.branchMode = router.url.startsWith('/branch/');
    this.form = this.fb.group({
      keyword: [''],
      category_id: [''],
      min_stock: [null],
      max_stock: [null]
    });
    const all = this.platformService?.channels || [];
    this.channels = all.filter(c => !!c.is_branch === this.branchMode);
    this.channelsHeaders = [...new Set(this.channels.map(item => item.platform))]
  }

  ngOnInit(): void {
    if (this.branchMode) {
      this.branchesFilter = this.channels;
      if (this.channels.length > 0) {
        this.channelSelected = this.channels[0].shop_ref;
        this.branchKeyword = this.channels[0].platform_name;
      }
    }
    this.getDataProduct()
  }

  getDataProduct() {
    if (this.branchMode) {
      this.getBranchProducts()
    } else {
      this.getHqProducts()
    }
  }

  private getHqProducts() {
    this.loading = true;
    const value = this.form.getRawValue()
    const cleanFilter = Object.fromEntries(
      Object.entries(value).filter(([key, value]) => value !== null && value !== '')
    );
    const filters = {
      ...cleanFilter,
      fields: 'sku,name,category,in_stock,prices',
      limit: this.limit,
      offset: this.offset
    }

    this.productApiService.getAllProduct(filters).then(async (res: any) => {
      if (res.data) {
        this.products = res.data;
        this.countDataSource = res.count;
        const uniqueSku = res.data.map((e: { sku: any; }) => e.sku)
        if (uniqueSku.length > 0) {
          this.stockPlatform = await this.getAllProductPlatform(uniqueSku)
        }

      } else {
        this.products = [];
        this.countDataSource = 0;
      }
      this.reformattingDataset()
      this.loading = false;
    })
  }

  private async getBranchProducts() {
    this.loading = true;
    this.products = [];
    this.stockPlatform = [];
    if (!this.channelSelected) {
      this.countDataSource = 0;
      this.reformattingDataset();
      this.loading = false;
      return;
    }

    const { keyword, category_id } = this.form.getRawValue();
    const filters: any = { channel: this.channelSelected, limit: this.limit, offset: this.offset };
    if (keyword) filters.keyword = keyword;
    if (category_id) filters.category_id = category_id;

    try {
      const channelRes: any = await this.productApiService.findProductChannel(filters);
      if (!channelRes?.success) {
        this.countDataSource = 0;
        this.reformattingDataset();
        this.loading = false;
        return;
      }
      this.countDataSource = channelRes.count || 0;
      const skus = (channelRes.data || []).map((e: any) => e.sku);
      if (skus.length === 0) {
        this.reformattingDataset();
        this.loading = false;
        return;
      }

      const branch = this.channels.find(c => c.shop_ref === this.channelSelected);
      const branchNumber = branch?.branch_number;
      const [productRes, stockPlatform, sabuysoftRes]: any[] = await Promise.all([
        this.productApiService.getAllProduct({
          sku: skus.join(),
          fields: 'sku,name,category,in_stock',
          limit: skus.length
        }),
        this.getAllProductPlatform(skus),
        branchNumber
          ? this.productApiService.findStockSabuysoft({ sku: skus.join(), branch: branchNumber })
          : Promise.resolve({ success: true, data: [] }),
      ]);
      this.stockPlatform = stockPlatform || [];
      const branchStockMap = new Map<string, number>(
        (sabuysoftRes?.data || []).map((e: any) => [e.sku, e.summary])
      );
      this.products = (productRes?.data || []).map((p: any) => ({
        ...p,
        branch_stock: branchStockMap.get(p.sku) ?? 0,
        has_sabuysoft: branchStockMap.has(p.sku),
      }));
    } catch {
      this.products = [];
      this.countDataSource = 0;
    } finally {
      this.reformattingDataset();
      this.loading = false;
    }
  }

  async getAllProductPlatform(sku: string[]) {
    const filters: any = {
      sku: sku.join(),
      fields: 'sku,channel,sellable,platform_product_id,platform_sku,updated'
    }
    if (this.branchMode) {
      filters.channel = this.channels.map(c => c.shop_ref).join();
    }
    return await this.productApiService.getAllProductPlatform(filters).then((res: any) => {
      if (res.success) {
        return res.data;
      }
    })
  }

  filterBranches() {
    const k = (this.branchKeyword || '').toLowerCase();
    this.branchesFilter = this.channels.filter(c =>
      (c.platform_name || '').toLowerCase().includes(k)
    );
    this.showBranchFilter = true;
  }

  selectBranch(branch: any) {
    this.channelSelected = branch.shop_ref;
    this.branchKeyword = branch.platform_name;
    this.branchesFilter = this.channels;
    this.showBranchFilter = false;
  }

  openBranchFilter() {
    this.branchesFilter = this.channels;
    this.showBranchFilter = true;
  }

  closeBranchFilter() {
    setTimeout(() => {
      this.showBranchFilter = false;
      const current = this.channels.find(c => c.shop_ref === this.channelSelected);
      if (current) this.branchKeyword = current.platform_name;
    }, 150);
  }

  callBackPageChange = (data: any): void => {
    if (this.offset !== data.offset || this.limit !== data.limit) {
      this.offset = data.offset;
      this.limit = data.limit;
      this.getDataProduct()
    }

    if (data.pageIndex !== undefined) {
      this.pageIndex = data.pageIndex;
    }
    if (data.limit !== undefined) {
      this.pageSize = data.limit;
    }
  }

  reformattingDataset() {
    this.datasets = this.products.map(item => {
      const stocks = this.stockPlatform.filter(el => el.sku === item.sku).map(el => {
        const ch = this.channels.find(channel => channel.shop_ref === el.channel);
        return {
          ...el,
          channel_name: ch?.platform_name,
          channel_logo_url: ch?.logo_url,
          platform: ch?.platform
        };
      }).filter(el => el.platform);

      let stockMismatch = false;
      let mismatchPlatform: string | null = null;
      if (this.branchMode && (item as any).has_sabuysoft) {
        const selectedEntry = stocks.find(s => s.channel === this.channelSelected);
        if (selectedEntry && Number(selectedEntry.sellable) !== Number((item as any).branch_stock)) {
          stockMismatch = true;
          mismatchPlatform = selectedEntry.platform;
        }
      }

      return { ...item, stocks, stockMismatch, mismatchPlatform };
    });
  }

  submitForm() {
    this.offset = 0;
    this.pageIndex = 0;
    this.getDataProduct()
  }

  getStockByPlatform(platform: string, stocks: any[]) {
    return stocks.filter(item => item.platform === platform)
  }

  openUpdateStockDialog(sku: string) {
    const product = this.products.find(item => item.sku === sku)
    const stocks = this.stockPlatform.filter(item => item.sku === sku)
    if (stocks.length === 0) return
    if (product) {
      const data: UpdateStockModalModel = {
        sku: sku,
        name: product?.name,
        stock: product?.in_stock,
        products: [{ sku }],
        channels: stocks.filter(item => this.channels.find(el => el.shop_ref === item.channel)).map(item => ({
          channel: item.channel,
          name: this.channels.find(el => el.shop_ref === item.channel)?.platform_name,
          platform: this.channels.find(el => el.shop_ref === item.channel)?.platform,
          stock: item.sellable,
          updated_date: item.updated.date
        }))
      }
      const dialogRef = this.dialog.open(UpdateStockModalComponent, {
        data,
        disableClose: true
      });
      dialogRef.afterClosed().subscribe(async (result: any) => {
        if (result.confirm) {

        }
      });
    }
  }

  openSyncProductDialog() {
    const dialogRef = this.dialog.open(SyncProductDialogComponent);

    dialogRef.afterClosed().subscribe((result: any) => {
    })
  }

  canAccess() {
    return this.authService.user?.role ? ['admin', 'marketing'].includes(this.authService.user.role) : false;
  }

  getTooltipText(product: any): string {
    return `ID: ${product.platform_product_id}\nSKU: ${product.platform_sku}`;
  }

}
